import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Button, Stack, Alert, CircularProgress } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { fetchApi } from '../hooks/useApi';

/**
 * AdminPanel - lists pending premium requests and lets an admin approve/deny them.
 *
 * Only rendered for admins (App.js checks the cognito:groups claim), but that is
 * presentation only — the backend endpoints enforce the admins group themselves
 * (a non-admin calling them gets 403), so showing this to a non-admin would just
 * yield failed calls, never unauthorized action.
 */
function AdminPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // userId currently being decided

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchApi('/admin/premium-requests');
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (err) {
      setError(err.status === 403 ? 'You are not authorized to view premium requests.'
        : (err.message || 'Could not load premium requests.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (userId, action) => {
    setBusy(userId);
    setError(null);
    try {
      await fetchApi(`/admin/premium-requests/${encodeURIComponent(userId)}/${action}`, { method: 'POST' });
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
    } catch (err) {
      setError(err.message || `Could not ${action} the request.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card elevation={3} sx={{ mt: 1 }}>
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AdminPanelSettingsIcon />
        <Typography variant="h3" component="h2" sx={{ color: '#fff' }}>Premium Requests</Typography>
      </Box>
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} /><Typography color="text.secondary">Loading requests…</Typography>
          </Stack>
        ) : requests.length === 0 ? (
          <Typography color="text.secondary">No pending premium requests.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>User</TableCell><TableCell>Requested</TableCell><TableCell align="right">Action</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.userId}>
                  <TableCell>{r.userId}</TableCell>
                  <TableCell>{r.requestedAt ? new Date(r.requestedAt * 1000).toLocaleString() : '--'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="contained" disabled={busy === r.userId} onClick={() => decide(r.userId, 'approve')}>Approve</Button>
                      <Button size="small" variant="outlined" color="error" disabled={busy === r.userId} onClick={() => decide(r.userId, 'deny')}>Deny</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminPanel;
